import { sql } from "drizzle-orm";
import {
  JobAdmissionError,
  getMsUntilNextMinute,
  isJobAdmissionKillSwitchActive,
  jsonByteLength,
  readPositiveIntegerEnv,
} from "./job-admission.js";

type DbLike = {
  execute: (query: ReturnType<typeof sql>) => Promise<unknown>;
  transaction?: <T>(callback: (tx: DbLike) => Promise<T>) => Promise<T>;
};

type WorkflowAdmissionCounts = {
  pending: number;
  running: number;
  pendingByName: number;
};

export interface WorkflowStartAdmissionOptions {
  db: DbLike;
  workflowName: string;
  args: unknown;
  env?: Record<string, string | undefined>;
  now?: () => Date;
}

const DEFAULT_WORKFLOW_PENDING_PER_APP = 1_000;
const DEFAULT_WORKFLOW_RUNNING_PER_APP = 50;
const DEFAULT_WORKFLOW_PENDING_PER_NAME = 300;
const DEFAULT_WORKFLOW_STARTS_PER_MINUTE_PER_APP = 120;
const DEFAULT_WORKFLOW_PAYLOAD_MAX_BYTES = 64 * 1024;

const WORKFLOW_ADMISSION_ENV_KEYS = [
  "GENCOW_JOB_ADMISSION_ENABLED",
  "GENCOW_JOB_ADMISSION_KILL_SWITCH",
  "GENCOW_JOB_KILL_SWITCH",
  "GENCOW_PLATFORM_JOB_KILL_SWITCH",
  "GENCOW_WORKFLOW_PENDING_PER_APP",
  "GENCOW_WORKFLOW_RUNNING_PER_APP",
  "GENCOW_WORKFLOW_PENDING_PER_NAME",
  "GENCOW_WORKFLOW_STARTS_PER_MINUTE_PER_APP",
  "GENCOW_WORKFLOW_STARTS_PER_MINUTE_PER_NAME",
  "GENCOW_WORKFLOW_PAYLOAD_MAX_BYTES",
  "GENCOW_JOB_PENDING_PER_APP",
  "GENCOW_JOB_CREATES_PER_MINUTE_PER_APP",
  "GENCOW_JOB_PAYLOAD_MAX_BYTES",
] as const;

function rows<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function numberFrom(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isMissingAdmissionSchemaError(error: unknown): boolean {
  const code =
    error && typeof error === "object" && "code" in error ? String((error as { code?: unknown }).code) : "";
  const cause =
    error && typeof error === "object" && "cause" in error ? (error as { cause?: unknown }).cause : null;
  const causeCode =
    cause && typeof cause === "object" && "code" in cause ? String((cause as { code?: unknown }).code) : "";
  const message = error instanceof Error ? error.message : String(error);
  return (
    code === "42P01" ||
    causeCode === "42P01" ||
    message.includes('relation "_gencow_job_admission_buckets" does not exist') ||
    message.includes("relation _gencow_job_admission_buckets does not exist")
  );
}

function isMissingWorkflowV2SchemaError(error: unknown): boolean {
  const code =
    error && typeof error === "object" && "code" in error ? String((error as { code?: unknown }).code) : "";
  const cause =
    error && typeof error === "object" && "cause" in error ? (error as { cause?: unknown }).cause : null;
  const causeCode =
    cause && typeof cause === "object" && "code" in cause ? String((cause as { code?: unknown }).code) : "";
  const message = error instanceof Error ? error.message : String(error);
  return (
    code === "42P01" ||
    code === "42703" ||
    causeCode === "42P01" ||
    causeCode === "42703" ||
    message.includes('relation "_gencow_workflow_runs_v2" does not exist') ||
    message.includes("relation _gencow_workflow_runs_v2 does not exist")
  );
}

function isWorkflowAdmissionEnabled(env: Record<string, string | undefined>): boolean {
  if ((env.GENCOW_JOB_ADMISSION_ENABLED ?? "").trim().toLowerCase() === "false") return false;
  if (env.GENCOW_APP_NAME) return true;
  return WORKFLOW_ADMISSION_ENV_KEYS.some((key) => env[key] != null && env[key] !== "");
}

function resolveWorkflowAdmissionPolicy(env: Record<string, string | undefined>) {
  return {
    pendingPerApp:
      readPositiveIntegerEnv(env, "GENCOW_WORKFLOW_PENDING_PER_APP") ??
      readPositiveIntegerEnv(env, "GENCOW_JOB_PENDING_PER_APP") ??
      DEFAULT_WORKFLOW_PENDING_PER_APP,
    runningPerApp:
      readPositiveIntegerEnv(env, "GENCOW_WORKFLOW_RUNNING_PER_APP") ?? DEFAULT_WORKFLOW_RUNNING_PER_APP,
    pendingPerName:
      readPositiveIntegerEnv(env, "GENCOW_WORKFLOW_PENDING_PER_NAME") ?? DEFAULT_WORKFLOW_PENDING_PER_NAME,
    startsPerMinutePerApp:
      readPositiveIntegerEnv(env, "GENCOW_WORKFLOW_STARTS_PER_MINUTE_PER_APP") ??
      readPositiveIntegerEnv(env, "GENCOW_JOB_CREATES_PER_MINUTE_PER_APP") ??
      DEFAULT_WORKFLOW_STARTS_PER_MINUTE_PER_APP,
    startsPerMinutePerName:
      readPositiveIntegerEnv(env, "GENCOW_WORKFLOW_STARTS_PER_MINUTE_PER_NAME") ??
      readPositiveIntegerEnv(env, "GENCOW_WORKFLOW_STARTS_PER_MINUTE_PER_APP") ??
      readPositiveIntegerEnv(env, "GENCOW_JOB_CREATES_PER_MINUTE_PER_APP") ??
      DEFAULT_WORKFLOW_STARTS_PER_MINUTE_PER_APP,
    payloadMaxBytes:
      readPositiveIntegerEnv(env, "GENCOW_WORKFLOW_PAYLOAD_MAX_BYTES") ??
      readPositiveIntegerEnv(env, "GENCOW_JOB_PAYLOAD_MAX_BYTES") ??
      DEFAULT_WORKFLOW_PAYLOAD_MAX_BYTES,
  };
}

async function countWorkflowV2Backlog(db: DbLike, workflowName: string): Promise<WorkflowAdmissionCounts> {
  const result = await db.execute(sql`
    SELECT
      COALESCE(SUM(CASE WHEN completed_at IS NULL AND status IN ('queued', 'waiting', 'sleeping') THEN 1 ELSE 0 END), 0)::int AS pending,
      COALESCE(SUM(CASE WHEN completed_at IS NULL AND status = 'running' THEN 1 ELSE 0 END), 0)::int AS running,
      COALESCE(SUM(CASE WHEN completed_at IS NULL AND status IN ('queued', 'waiting', 'sleeping') AND workflow_name = ${workflowName} THEN 1 ELSE 0 END), 0)::int AS pending_by_name
    FROM _gencow_workflow_runs_v2
  `);
  const row = rows<{ pending: unknown; running: unknown; pending_by_name: unknown }>(result)[0];
  return {
    pending: numberFrom(row?.pending),
    running: numberFrom(row?.running),
    pendingByName: numberFrom(row?.pending_by_name),
  };
}

async function countWorkflowV1Backlog(db: DbLike, workflowName: string): Promise<WorkflowAdmissionCounts> {
  const result = await db.execute(sql`
    SELECT
      COALESCE(SUM(CASE WHEN status = 'pending' THEN 1 ELSE 0 END), 0)::int AS pending,
      COALESCE(SUM(CASE WHEN status = 'running' THEN 1 ELSE 0 END), 0)::int AS running,
      COALESCE(SUM(CASE WHEN status = 'pending' AND name = ${workflowName} THEN 1 ELSE 0 END), 0)::int AS pending_by_name
    FROM _gencow_workflows
  `);
  const row = rows<{ pending: unknown; running: unknown; pending_by_name: unknown }>(result)[0];
  return {
    pending: numberFrom(row?.pending),
    running: numberFrom(row?.running),
    pendingByName: numberFrom(row?.pending_by_name),
  };
}

async function countWorkflowBacklog(db: DbLike, workflowName: string): Promise<WorkflowAdmissionCounts> {
  try {
    return await countWorkflowV2Backlog(db, workflowName);
  } catch (error) {
    if (!isMissingWorkflowV2SchemaError(error)) throw error;
    return countWorkflowV1Backlog(db, workflowName);
  }
}

async function consumeWorkflowRateBucket(
  db: DbLike,
  scope: string,
  limit: number,
  now: Date,
): Promise<boolean> {
  try {
    const result = await db.execute(sql`
      INSERT INTO _gencow_job_admission_buckets (scope, bucket_start, count, updated_at)
      VALUES (${scope}, date_trunc('minute', ${now.toISOString()}::timestamptz), 1, NOW())
      ON CONFLICT (scope, bucket_start) DO UPDATE
        SET count = _gencow_job_admission_buckets.count + 1,
            updated_at = NOW()
        WHERE _gencow_job_admission_buckets.count < ${limit}
      RETURNING count
    `);
    return rows(result).length > 0;
  } catch (error) {
    if (!isMissingAdmissionSchemaError(error)) throw error;
    throw new JobAdmissionError(
      "job_admission_disabled",
      "workflow admission bucket schema is not initialized",
    );
  }
}

async function acquireWorkflowStartAdmissionLock(db: DbLike): Promise<void> {
  await db.execute(sql`SELECT pg_advisory_xact_lock(hashtextextended('workflow:create', 0))`);
}

export async function applyWorkflowStartAdmission(options: WorkflowStartAdmissionOptions): Promise<void> {
  const env = options.env ?? (typeof process === "undefined" ? {} : process.env);
  if (!isWorkflowAdmissionEnabled(env)) return;

  if (isJobAdmissionKillSwitchActive(env, `workflow:create:name:${options.workflowName}`)) {
    throw new JobAdmissionError("job_admission_disabled", "workflow start admission is disabled");
  }

  const policy = resolveWorkflowAdmissionPolicy(env);
  const payloadBytes = jsonByteLength(options.args);
  if (payloadBytes > policy.payloadMaxBytes) {
    throw new JobAdmissionError("job_payload_too_large", "workflow start payload is too large", {
      details: { limitBytes: policy.payloadMaxBytes, payloadBytes },
    });
  }

  await acquireWorkflowStartAdmissionLock(options.db);

  const counts = await countWorkflowBacklog(options.db, options.workflowName);
  if (counts.pending >= policy.pendingPerApp) {
    throw new JobAdmissionError("job_backlog_limit_exceeded", "workflow pending backlog limit exceeded", {
      details: { limit: policy.pendingPerApp, pending: counts.pending },
    });
  }
  if (counts.running >= policy.runningPerApp) {
    throw new JobAdmissionError("job_backlog_limit_exceeded", "workflow running backlog limit exceeded", {
      details: { limit: policy.runningPerApp, running: counts.running },
    });
  }
  if (counts.pendingByName >= policy.pendingPerName) {
    throw new JobAdmissionError(
      "job_backlog_limit_exceeded",
      "workflow name pending backlog limit exceeded",
      {
        details: {
          limit: policy.pendingPerName,
          pending: counts.pendingByName,
          workflowName: options.workflowName,
        },
      },
    );
  }

  const now = options.now?.() ?? new Date();
  const consumed = await consumeWorkflowRateBucket(
    options.db,
    "workflow:create",
    policy.startsPerMinutePerApp,
    now,
  );
  if (!consumed) {
    throw new JobAdmissionError("job_rate_limited", "workflow start rate limit exceeded", {
      retryAfterMs: getMsUntilNextMinute(now),
      details: { limit: policy.startsPerMinutePerApp },
    });
  }

  const nameConsumed = await consumeWorkflowRateBucket(
    options.db,
    `workflow:create:name:${options.workflowName}`,
    policy.startsPerMinutePerName,
    now,
  );
  if (!nameConsumed) {
    throw new JobAdmissionError("job_rate_limited", "workflow name start rate limit exceeded", {
      retryAfterMs: getMsUntilNextMinute(now),
      details: { limit: policy.startsPerMinutePerName, workflowName: options.workflowName },
    });
  }
}

export async function runWorkflowAdmissionCriticalSection<T>(
  db: DbLike,
  callback: (tx: DbLike) => Promise<T>,
): Promise<T> {
  if (db.transaction) {
    return db.transaction(callback);
  }
  return callback(db);
}
